import { cva } from 'class-variance-authority';
import Inputmask from 'inputmask';
import type { ChangeEvent, DetailedHTMLProps, FC, ForwardedRef, InputHTMLAttributes } from 'react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import { getMask, maskOnChangeFunction } from './helpers';
import { MaskWithOptions } from './types';

export type InputProps = {
  errorMsg?: string;
  wrapperClassName?: string;
  mask?: MaskWithOptions;
} & DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>;

const styles = cva(
  [
    'h-10 w-full rounded-lg border-primary-border bg-primary-background px-3 py-2 text-primary-900 shadow-xs placeholder:text-other-textPlaceholder',
    'disabled:cursor-not-allowed disabled:text-primary-900/25 ',
    'focus:shadow-none focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2',
  ],
  {
    variants: {
      hasError: {
        true: 'border-red-500',
      },
    },
  }
);

const InputComp = ({ errorMsg, wrapperClassName, className, mask, onChange, ...props }: InputProps, forwardRef: ForwardedRef<HTMLInputElement>) => {
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (mask && ref.current) {
      Inputmask({
        ...getMask(mask),
        oncleared: () => {
          ref.current!.value = '';
        },
      }).mask(ref.current);
    }
  }, [mask?.alias, mask?.mask]);

  const internalOnChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      if (mask && onChange) return maskOnChangeFunction(e, onChange);

      if (onChange) return onChange(e);

      return undefined;
    },
    [mask?.alias, mask?.mask, onChange]
  );

  useImperativeHandle(forwardRef, () => ref.current as HTMLInputElement);

  return (
    <div className={twMerge('relative w-full text-[12px] leading-[17px]', wrapperClassName)}>
      <input ref={ref} onChange={internalOnChange} type='text' className={twMerge(styles({ hasError: !!errorMsg }), className)} spellCheck {...props} />
      {errorMsg ? (
        <p role='alert' className='text-red-500'>
          {errorMsg}
        </p>
      ) : null}
    </div>
  );
};

export const Input = forwardRef<HTMLInputElement, InputProps>(InputComp) as FC<InputProps>;
